import React from 'react';
import Box from '@mui/material/Box';
import FieldBox from './FieldBox';

// One rendered PDF page plus the blocks placed on it. Double-click drops a
// block; dragging the background box-selects.
export default function CanvasPage({
  page, pageIndex, widthPts, heightPts, scale, fields, selectedSet, roleLabel,
  onSelect, onBeginDrag, onMoveBy, onResize, onAddAt, onMarquee, onClearSelection,
}) {
  const ref = React.useRef(null);
  const boxRef = React.useRef(null);
  const marq = React.useRef(null);
  const [rect, setRect] = React.useState(null); // marquee rect in px {l,t,w,h}

  React.useEffect(() => {
    (async () => {
      const viewport = page.getViewport({ scale });
      const c = ref.current; if (!c) return;
      c.width = viewport.width; c.height = viewport.height;
      try { await page.render({ canvasContext: c.getContext('2d'), viewport }).promise; } catch (e) { /* superseded */ }
    })();
  }, [page, scale]);

  // Marquee select — pointerdown on the page background (field pointerdowns stop
  // propagation, so they never reach here).
  const bgDown = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    marq.current = { x0: e.clientX - r.left, y0: e.clientY - r.top, additive: e.shiftKey || e.metaKey || e.ctrlKey, moved: false };
    boxRef.current.setPointerCapture?.(e.pointerId);
  };
  const bgMove = (e) => {
    if (!marq.current) return;
    const r = boxRef.current.getBoundingClientRect();
    const x1 = e.clientX - r.left, y1 = e.clientY - r.top;
    const { x0, y0 } = marq.current;
    if (Math.abs(x1 - x0) + Math.abs(y1 - y0) > 4) marq.current.moved = true;
    setRect({ l: Math.min(x0, x1), t: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) });
  };
  const bgUp = () => {
    const m = marq.current; marq.current = null; setRect(null);
    if (!m) return;
    if (!m.moved) { onClearSelection(m.additive); return; } // simple click on bg
    const box = rect;
    if (!box) return;
    const rectPts = {
      minX: box.l / scale, maxX: (box.l + box.w) / scale,
      maxY: heightPts - box.t / scale, minY: heightPts - (box.t + box.h) / scale,
    };
    onMarquee(pageIndex, rectPts, m.additive);
  };

  return (
    <Box ref={boxRef} onPointerDown={bgDown} onPointerMove={bgMove} onPointerUp={bgUp}
      sx={{ position: 'relative', width: widthPts * scale, mx: 'auto', mb: 3, borderRadius: 1, overflow: 'visible', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', bgcolor: '#fff' }}
      onDoubleClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onAddAt(pageIndex, (e.clientX - r.left) / scale, heightPts - (e.clientY - r.top) / scale);
      }}>
      <canvas ref={ref} style={{ display: 'block', borderRadius: 6 }} />
      {fields.filter((f) => f.page === pageIndex).map((f) => (
        <FieldBox key={f.id} field={f} pageHeightPts={heightPts} scale={scale} selected={selectedSet.has(f.id)}
          roleLabel={roleLabel} onSelect={onSelect} onBeginDrag={onBeginDrag} onMoveBy={onMoveBy} onResize={onResize} />
      ))}
      {rect && (
        <div style={{ position: 'absolute', left: rect.l, top: rect.t, width: rect.w, height: rect.h,
          border: '1.5px solid #003366', background: 'rgba(0,51,102,0.08)', pointerEvents: 'none', zIndex: 40 }} />
      )}
      <Box sx={{ position: 'absolute', bottom: 6, right: 10, fontSize: 11, color: 'text.disabled', pointerEvents: 'none' }}>Page {pageIndex + 1}</Box>
    </Box>
  );
}
